import { useState } from "react";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Send, ShieldCheck } from "lucide-react";

const TOPIC_OPTIONS = [
  { value: "general", label: "General inquiry" },
  { value: "counterclaim", label: "Counterclaim / foreclosure relief" },
  { value: "membership", label: "Membership questions" },
  { value: "sheriff_sale", label: "Sheriff sale (urgent)" },
  { value: "mediation", label: "Mediation & arbitration" },
  { value: "marketplace", label: "Homes & land marketplace" },
  { value: "press", label: "Press / media" },
  { value: "other", label: "Other" },
];

const URGENCY_OPTIONS = [
  { value: "routine", label: "Routine — within 1 business day" },
  { value: "soon", label: "Soon — within 48 hours" },
  { value: "urgent", label: "Urgent — sheriff sale or court date this week" },
];

const STATE_HINT = "If outside the U.S., enter Country.";

export default function HomeContactForm() {
  const initial = {
    name: "", email: "", phone: "",
    state: "", city: "",
    topic: "general", urgency: "routine",
    property_address: "",
    in_foreclosure: false,
    sheriff_sale_scheduled: false,
    sheriff_sale_date: "",
    mortgage_balance: "",
    subject: "",
    message: "",
    consent: false,
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept the privacy notice"); return; }
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required");
      return;
    }
    setBusy(true);
    const topicLabel = TOPIC_OPTIONS.find((o) => o.value === form.topic)?.label || form.topic;
    const urgencyLabel = URGENCY_OPTIONS.find((o) => o.value === form.urgency)?.label || form.urgency;
    const subject = form.subject?.trim()
      ? `[${urgencyLabel.split(" — ")[0]} · ${topicLabel}] ${form.subject}`
      : `[${urgencyLabel.split(" — ")[0]} · ${topicLabel}] ${form.name}`;
    const compiledMessage = [
      `Phone: ${form.phone || "—"}`,
      `Location: ${form.city || "—"}, ${form.state || "—"}`,
      `Property address: ${form.property_address || "—"}`,
      `Currently in foreclosure: ${form.in_foreclosure ? "YES" : "no"}`,
      `Sheriff sale scheduled: ${form.sheriff_sale_scheduled ? `YES (${form.sheriff_sale_date || "date TBD"})` : "no"}`,
      `Approx mortgage balance: ${form.mortgage_balance ? `$${Number(form.mortgage_balance).toLocaleString()}` : "—"}`,
      `Topic: ${topicLabel}`,
      `Urgency: ${urgencyLabel}`,
      "",
      "MESSAGE:",
      form.message,
    ].join("\n");

    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject,
        message: compiledMessage,
      });
      toast.success("Message received. A trustee will respond.");
      setForm(initial);
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="home-contact-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="hc-name">Full name *</Label>
          <Input id="hc-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="hc-name" className="bg-white" />
        </div>
        <div>
          <Label htmlFor="hc-email">Email *</Label>
          <Input id="hc-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="hc-email" className="bg-white" />
        </div>
        <div>
          <Label htmlFor="hc-phone">Phone</Label>
          <Input id="hc-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="hc-phone" className="bg-white" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="hc-city">City</Label>
            <Input id="hc-city" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="hc-city" className="bg-white" />
          </div>
          <div>
            <Label htmlFor="hc-state" title={STATE_HINT}>State</Label>
            <Input id="hc-state" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="hc-state" className="bg-white" />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Topic</Label>
          <Select value={form.topic} onValueChange={(v) => set("topic", v)}>
            <SelectTrigger data-testid="hc-topic" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TOPIC_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Urgency</Label>
          <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
            <SelectTrigger data-testid="hc-urgency" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {URGENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="hc-property">Property address (if applicable)</Label>
        <Input id="hc-property" value={form.property_address} onChange={(e) => set("property_address", e.target.value)} data-testid="hc-property" className="bg-white" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="hc-foreclosure-row">
          <Checkbox checked={form.in_foreclosure} onCheckedChange={(v) => set("in_foreclosure", !!v)} />
          <span className="text-sm">Currently in foreclosure</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="hc-sheriff-row">
          <Checkbox checked={form.sheriff_sale_scheduled} onCheckedChange={(v) => set("sheriff_sale_scheduled", !!v)} />
          <span className="text-sm">Sheriff sale scheduled</span>
        </label>
      </div>

      {form.sheriff_sale_scheduled && (
        <div>
          <Label htmlFor="hc-sheriff-date">Sheriff sale date</Label>
          <Input id="hc-sheriff-date" type="date" value={form.sheriff_sale_date} onChange={(e) => set("sheriff_sale_date", e.target.value)} data-testid="hc-sheriff-date" className="bg-white" />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="hc-balance">Approx mortgage balance (USD)</Label>
          <Input id="hc-balance" type="number" value={form.mortgage_balance} onChange={(e) => set("mortgage_balance", e.target.value)} data-testid="hc-balance" className="bg-white" />
        </div>
        <div>
          <Label htmlFor="hc-subject">Subject (optional)</Label>
          <Input id="hc-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="hc-subject" className="bg-white" />
        </div>
      </div>

      <div>
        <Label htmlFor="hc-message">Message *</Label>
        <Textarea id="hc-message" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="hc-message" className="bg-white" placeholder="Briefly describe your situation, what stage you're at, and what outcome you need." />
      </div>

      <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="hc-consent-row">
        <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
        <span className="text-[color:var(--text-secondary)]">
          I understand Azure Gypsy Guild trustees will receive this message securely. The Guild does not share my information with third parties.
        </span>
      </label>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
        <p className="text-xs text-[color:var(--text-secondary)] flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-[color:var(--bright-green)]" />
          Routed directly to the trustee admin portal — no email used.
        </p>
        <Button type="submit" disabled={busy} size="lg" className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="hc-submit-btn">
          <Send className="mr-2 h-4 w-4" />{busy ? "Sending…" : "Send to a trustee"}
        </Button>
      </div>
    </form>
  );
}
